'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/lib/stores/useAuthStore'

type SignUpMetadata = {
  full_name?: string
  invited_by?: string
  approval_status?: 'pending' | 'approved' | 'rejected'
}

type AuthContextType = {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ success: boolean; user: User | null }>
  signUp: (email: string, password: string, metadata?: SignUpMetadata) => Promise<User | void>
  signOut: () => Promise<{ success: boolean }>
  resetPassword: (email: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const { fetchProfile } = useAuthStore()
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    // Check active sessions and sets the user
    const initAuth = async () => {
      try {
        // Check if we're in an auth flow (sign up, invite verification, etc.)
        const isAuthFlow = 
          typeof window !== 'undefined' && (
            window.location.pathname.includes('/auth/') ||
            window.location.pathname === '/' ||
            window.location.pathname.includes('/sign-up')
          );
        
        const isDashboardRoute = 
          typeof window !== 'undefined' && 
          window.location.pathname.includes('/dashboard');

        // Use getUser instead of getSession for better security
        const { data: { user }, error } = await supabase.auth.getUser()
        
        if (error) {
          // If we're in an auth flow, this error is expected
          if (isAuthFlow && error.message?.includes('session')) {
            // Just log it as debug info
            console.debug('Auth flow info: No user session yet (expected during auth flow)');
          } else {
            console.error('Error getting user:', error)
          }
          
          setUser(null)
          
          // If user is trying to access dashboard but not logged in, redirect to sign-in
          if (isDashboardRoute && typeof window !== 'undefined') {
            window.location.href = '/auth/sign-in';
            return;
          }
        } else {
          setUser(user)
          
          // If we have a user, fetch their profile
          if (user) {
            await fetchProfile()
            
            // If user is on auth page but already logged in, redirect to dashboard
            if (isAuthFlow && typeof window !== 'undefined') {
              window.location.href = '/dashboard';
              return;
            }
          } else if (isDashboardRoute && typeof window !== 'undefined') {
            // No user but trying to access dashboard
            window.location.href = '/auth/sign-in';
            return;
          }
        }
        
        setInitialized(true)
        setLoading(false)
      } catch (error) {
        if (typeof window !== 'undefined' && window.location.pathname.includes('/auth/')) {
          // If we're in an auth flow, log errors as debug info
          console.debug('Auth flow info: Error during auth initialization (expected during auth flow)');
        } else {
          console.error('Error initializing auth:', error)
        }
        setLoading(false)
        setInitialized(true)
      }
    }
    
    initAuth()

    // Listen for changes on auth state (signed in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Avoid logging during sign-up and initial page load
      const shouldLog = !window.location.pathname.includes('/auth/sign-up');
      if (shouldLog) {
        console.log('Auth state changed:', event, session?.user?.id);
      }
      
      try {
        // Always use getUser to verify the session
        const { data, error } = await supabase.auth.getUser()
        
        if (error && !window.location.pathname.includes('/auth/')) {
          console.error('Error getting user on auth state change:', error);
        }
        
        const currentUser = data?.user || null
        setUser(currentUser)
        
        // If we have a user, fetch their profile
        if (currentUser) {
          await fetchProfile()
        }
      } catch (error) {
        // Only log errors if not in auth flow
        if (!window.location.pathname.includes('/auth/')) {
          console.error('Error in auth state change handler:', error);
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [fetchProfile])

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      })
      
      if (error) {
        console.error('Sign in error:', error)
        throw error
      }
      
      if (!data?.session) {
        throw new Error('Failed to create session')
      }
      
      // Verify the user with getUser
      const { data: userData } = await supabase.auth.getUser()
      setUser(userData.user)
      
      // Fetch profile after successful sign in
      await fetchProfile()
      
      return { success: true, user: userData.user }
    } catch (err) {
      console.error('Sign in function error:', err)
      throw err
    }
  }

  const signUp = async (email: string, password: string, metadata?: SignUpMetadata) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })
      
      if (error) {
        console.error('Supabase sign up error:', error)
        throw error
      }
      
      if (!data?.user) {
        throw new Error('Failed to create user account')
      }
      
      // Verify the user with getUser
      const { data: userData } = await supabase.auth.getUser()
      setUser(userData.user)
      
      // Fetch profile after successful sign up
      await fetchProfile()
      
      return data.user
    } catch (err) {
      console.error('Sign up function error:', err)
      throw err
    }
  }

  const signOut = async () => {
    try {
      // First clear all local state
      setUser(null)
      useAuthStore.setState({ profile: null, user: null, socialLinks: null, userPreferences: null })
      
      // Clear any local storage items related to auth
      if (typeof window !== 'undefined') {
        // More comprehensive cleanup of localStorage
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && (key.includes('supabase') || key.includes('sb-'))) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
        
        // Clear all supabase-related cookies
        document.cookie.split(";").forEach((c) => {
          if (c.includes('supabase') || c.includes('sb-')) {
            document.cookie = c
              .replace(/^ +/, "")
              .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
          }
        });
      }
      
      // Then sign out with Supabase (this will trigger the onAuthStateChange event)
      const { error } = await supabase.auth.signOut({ scope: 'global' })
      if (error) throw error
      
      // Force a small delay to ensure state updates are processed
      await new Promise(resolve => setTimeout(resolve, 200))
      
      return { success: true }
    } catch (err) {
      console.error('Sign out error:', err)
      // Even if there's an error, consider it a success if we've cleared local state
      return { success: true }
    }
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })
    if (error) throw error
  }

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {initialized && children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 